'use client';

import { useEffect, useState } from 'react';

export interface LiveDurationProps {
  from: string | Date;
  /** Optional fixed end time. When omitted, the duration updates against the current time. */
  to?: string | Date;
  /** Include seconds in the elapsed duration. Defaults to true. */
  showSeconds?: boolean;
}

function formatElapsed(start: string, end: string, showSeconds: boolean): string {
  const ms = Math.abs(new Date(end).getTime() - new Date(start).getTime());
  const seconds = Math.floor(ms / 1000);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || !showSeconds) parts.push(`${minutes}m`);
  if (showSeconds) parts.push(`${secs}s`);
  return parts.join(' ');
}

/**
 * Live-ticking duration display. Shows elapsed time from a given start time,
 * updating at the displayed precision until an optional fixed end time is supplied.
 */
export function LiveDuration({ from, to, showSeconds = true }: LiveDurationProps) {
  const [now, setNow] = useState(() => new Date().toISOString());

  useEffect(() => {
    if (to) return;

    const tickMs = showSeconds ? 1000 : 30_000;
    const interval = setInterval(() => setNow(new Date().toISOString()), tickMs);
    return () => clearInterval(interval);
  }, [showSeconds, to]);

  const fromStr = from instanceof Date ? from.toISOString() : from;
  const toStr = to instanceof Date ? to.toISOString() : to;

  return <span>{formatElapsed(fromStr, toStr ?? now, showSeconds)}</span>;
}
